import { css } from "@emotion/css";
import { useEffect, useState } from "react";

import { countryTypes, stateProvinceTypes } from "./types";
import { Loader } from "../../components/loaders/loading/Loader";
import { Text } from "../../foundations/typography/Text";

export interface DriversLicenseProps {
  country: countryTypes;
  region: stateProvinceTypes;
  certnImagesBucket?: string;
  title: string;
  testID?: string;
}

const defaultBucket =
  "https://certn-api-s3-certn-images-ca-central-1-production.s3.ca-central-1.amazonaws.com/";

export function DriversLicense(props: DriversLicenseProps) {
  const { testID = "drivers-license" } = props;
  const [hasImage, setHasImage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const bucket = props.certnImagesBucket || defaultBucket;
  const url = `${bucket}licenses/${props.country}/${props.region}.png`;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const response = await fetch(url);
      if (!response.ok) {
        setHasImage(response.ok);
        throw new Error();
      } else setHasImage(response.ok);
    };

    fetchData()
      .catch(() => {
        console.error(
          `Can't access image for ${props.country}-${props.region}`
        );
      })
      .finally(() => setIsLoading(false));
  }, [props.country, props.region, url]);

  // design asked to remove preview if no image exists in S3
  if (!hasImage) return null;

  return (
    <div
      className={css(styles.wrapper, {
        backgroundColor: "var(--background-accent-neutral-weak)",
      })}
      data-testid={testID}
    >
      <Text style="global/heading-small" color="text-weak">
        {props.title}
      </Text>
      <div
        className={css(styles.license, {
          justifyContent: isLoading ? "center" : undefined,
        })}
      >
        {isLoading ? (
          <Loader size="medium" />
        ) : (
          <img alt="example license" src={url} />
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: css({
    height: "198px",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "var(--space-3)",
    paddingRight: "var(--space-4)",
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    borderRadius: "var(--border-radius-2)",
    gap: "var(--space-1)",
  }),
  license: css({
    overflow: "hidden",
    width: "238px",
    height: "150px",
    display: "flex",
    borderRadius: "var(--border-radius-2)",
  }),
};
